import React, { useState, useCallback, useEffect } from 'react';

import fetchEndpoint from '../../../services/fetchEndpoint';
import { getLegendValues, setHeatmapXMargins } from '../Charts/Heatmap/HeatmapHelpers';

import {
  getIdentifiersList,
  getSecondUrl,
  getRemainingPages,
  chunkCommaSeparatedIds,
} from '../Charts/Common/MultipleRequestsHelpers';

import { userAppContext } from '../../../context/Context';

interface UseHeatmapChartProps {
  chartInfo: any;
  initialChartData: any[];
  keySearch: any;
  source: any;
  baseUrl: any;
  parent: any;
  pageSize: number;
  view: any;
  getHeatmapData: any;
  unifyData: any;
  width: number;
  isTopAxisDiseases: boolean;
  uniqueIdentViewsVariables: any;
  names: any;
}

function useHeatmapChart({
  chartInfo,
  initialChartData,
  keySearch,
  source,
  baseUrl,
  parent,
  pageSize,
  view,
  getHeatmapData,
  unifyData,
  width,
  isTopAxisDiseases,
  uniqueIdentViewsVariables,
  names,
}: UseHeatmapChartProps) {
  const fetchAll = pageSize === -1;
  const effectivePageSize = fetchAll ? 100 : pageSize;
  const [identReqParam, setIdentReqParam] = useState('');
  const [chartData, setChartData] = useState(initialChartData);
  const [legendValues, setLegendValues] = useState({ min: 0, max: 1, divergeAt: 0.5 });
  const [isPayloadEmpty, setIsPayloadEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [height, setHeight] = useState<number | null>(null);
  const [xMargin, setXMargin] = useState<number | null>(null);
  const [totalResults, setTotalResults] = useState(0);

  const { authorization, setIsAuthenticated, setGroups, setAuthError, setAuthorization } =
    userAppContext() || {};

  const getIdentReqData = useCallback(async () => {
    setIsLoading(true);

    const orderByParam = chartInfo.variable;
    const identName = uniqueIdentViewsVariables[parent].identList[view];

    const firstUrl = `${baseUrl}?source=${source}&${keySearch}&page_number=${0}&page_size=${effectivePageSize}&order_by=${orderByParam}&order=desc`;
    const firstResponse = await fetchEndpoint(firstUrl, authorization, setIsAuthenticated);
    if (firstResponse === undefined) {
      setIsLoading(false);
      return;
    }

    const firstData = firstResponse.payload || [];
    const totalElements = firstResponse?.paging?.totalElements ?? firstData.length ?? 0;
    setTotalResults(totalElements);

    if (firstData.length === 0) {
      setIsPayloadEmpty(true);
      setIsLoading(false);
      return;
    }
    setIsPayloadEmpty(false);

    let identifiers = getIdentifiersList(firstData, identName);
    if (fetchAll) {
      const totalPages = Math.ceil(totalElements / effectivePageSize);
      for (let currentPage = 1; currentPage < totalPages; currentPage += 1) {
        const pagedUrl = `${baseUrl}?source=${source}&${keySearch}&page_number=${currentPage}&page_size=${effectivePageSize}&order_by=${orderByParam}&order=desc`;
        const pagedResponse = await fetchEndpoint(pagedUrl, authorization, setIsAuthenticated);
        if (pagedResponse?.payload?.length) {
          identifiers = [...identifiers, ...getIdentifiersList(pagedResponse.payload, identName)];
        }
      }
    }

    setIdentReqParam(Array.from(new Set(identifiers)).join(','));
  }, [source, keySearch, view, chartInfo, pageSize, fetchAll, effectivePageSize, parent, baseUrl]);

  useEffect(() => {
    getIdentReqData();

    return () => {
      setChartData(initialChartData);
      setLegendValues({ min: 0, max: 1, divergeAt: 0.5 });
      setIdentReqParam('');
      setIsLoading(false);
    };
  }, [getIdentReqData]);

  useEffect(() => {
    if (!identReqParam) {
      return;
    }

    let cancelled = false;

    const identParam = uniqueIdentViewsVariables[parent].secondReqIdent[view];
    const idChunks =
      typeof identParam === 'string'
        ? chunkCommaSeparatedIds(identReqParam, 1800)
        : [identReqParam];

    const run = async () => {
      setIsLoading(true);
      setChartData([]);

      const orderByParam = chartInfo.variable;
      const mappingVariables = uniqueIdentViewsVariables[parent].mappingIdents[view];
      const model = uniqueIdentViewsVariables[parent].model;

      let accumulated: any[] = [];

      for (const chunk of idChunks) {
        let pageNum = 0;
        let hasMorePages = true;
        while (hasMorePages) {
          if (cancelled) {
            return;
          }

          const pagingInfo = {
            currentPageNumber: pageNum,
            pageSize: effectivePageSize,
            remainingPages: undefined,
            totalElements: undefined,
          };
          const url = getSecondUrl(baseUrl, source, keySearch, identParam, chunk, pagingInfo, orderByParam);

          let responseData;
          try {
            responseData = await fetchEndpoint(url, authorization, setIsAuthenticated);
          } catch {
            if (!cancelled) {
              setIsLoading(false);
            }
            return;
          }

          if (cancelled) {
            return;
          }
          if (responseData === undefined) {
            setIsLoading(false);
            return;
          }

          const data = responseData.payload;
          const currentPagingInfo = responseData.paging;

          if (!currentPagingInfo) {
            break;
          }

          const transformedHeatmapChartData = getHeatmapData(data, mappingVariables, model, chartInfo.variable);
          accumulated = unifyData(accumulated, transformedHeatmapChartData);

          const heightToAdd = isTopAxisDiseases ? 260 : 190;
          const maxDataPointsPerRow =
            accumulated.length > 0 ? Math.max(...accumulated.map(row => row.data.length)) : 0;
          const baseRowHeight = 75;
          const adjustedRowHeight = maxDataPointsPerRow > 50 ? baseRowHeight + 25 : baseRowHeight;
          const calculatedHeight = accumulated.length * adjustedRowHeight + heightToAdd;
          setHeight(calculatedHeight);

          setChartData(accumulated);
          setXMargin(() => setHeatmapXMargins(width, identReqParam.split(',')));

          getLegendValues(chartInfo.variable, data, currentPagingInfo, setLegendValues);

          const remaining = getRemainingPages(
            pageNum + 1,
            currentPagingInfo.totalElements,
            currentPagingInfo.pageSize,
          );
          if (remaining === 0) {
            hasMorePages = false;
          } else {
            pageNum += 1;
          }
        }
      }

      if (!cancelled) {
        setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    identReqParam,
    chartInfo.variable,
    parent,
    view,
    baseUrl,
    source,
    keySearch,
    width,
    isTopAxisDiseases,
    effectivePageSize,
    getHeatmapData,
    unifyData,
    chartInfo,
    authorization,
    setIsAuthenticated,
  ]);

  return {
    chartData,
    isLoading,
    isPayloadEmpty,
    legendValues,
    height,
    identReqParam,
    xMargin,
    setXMargin,
    totalResults,
  };
}

export default useHeatmapChart;
